import React, { useMemo } from 'react'
import { Stack, Typography } from '@sureco/design-system'
import dayjs from 'dayjs'
import { AgGridReact } from '@ag-grid-community/react'
import {
  ColDef,
  GridApi,
  SelectionChangedEvent,
  StatusPanelDef,
} from '@ag-grid-community/core'
import GridNoResultsOverlay from 'components/GridNoResultsOverlay/GridNoResultsOverlay'
import { CopyOutlined, CheckOutlined } from '@ant-design/icons'
import { Link } from 'react-router'
import _ from 'lodash'
import { Person } from 'types/person'

const DateRenderer = ({ value }: { value: string }) => {
  return value ? dayjs.utc(value).format('YYYY/MM/DD') : ''
}

const IdRenderer = ({ value }: { value: string }) => {
  const [copied, setCopied] = React.useState(false)
  if (!value) return null

  const formattedId = `${value.slice(0, 6)}...${value.slice(-6)}`
  const handleCopy = () => {
    navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <Stack direction="row" space={8} style={{ alignItems: 'center' }}>
      <Typography
        size={14}
        style={{
          cursor: 'pointer',
          color: '#00a0a8',
          transition: 'color 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
        onClick={handleCopy}
      >
        {formattedId} {copied ? <CheckOutlined /> : <CopyOutlined />}
      </Typography>
    </Stack>
  )
}

const NameRenderer = ({ value, data }: { value: string; data: Person }) => {
  if (!value) return null

  const isTestUser = data.isTestUser === true
  const color = isTestUser ? 'red' : 'info'

  return (
    <Link
      to={`/employees/${data.employeePublicKey}/coverage`}
      style={{ textDecoration: 'none' }}
      target="_blank"
    >
      <Typography
        size={14}
        color={color}
        style={{
          cursor: 'pointer',
        }}
      >
        {value}
      </Typography>
    </Link>
  )
}

const CustomStatusBarComponent = (props: {
  api: GridApi
  total: number
  totalLabel: string
}) => {
  const { api, total, totalLabel } = props
  const filteredCount = api?.getDisplayedRowCount() || 0
  const selectedCount = api?.getSelectedRows()?.length || 0

  return (
    <div style={{ padding: '10px 20px' }}>
      <Typography size={14} color="secondary">
        {selectedCount > 0 ? `${selectedCount} selected / ` : ''}
        {filteredCount} {totalLabel} of {total} total
      </Typography>
    </div>
  )
}

interface EnrollmentOutstandingTableProps {
  members: Person[]
  total: number
  onSelectionChanged: (selectedRows: Person[]) => void
  onGridReady: (params: any) => void
  quickFilterText?: string
  totalLabel?: string
}

export default function EnrollmentOutstandingTable({
  members,
  total,
  onSelectionChanged,
  onGridReady,
  quickFilterText = '',
  totalLabel = 'outstanding',
}: EnrollmentOutstandingTableProps) {
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      filterParams: {
        buttons: ['reset', 'apply'],
      },
    }),
    []
  )

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: '',
        field: 'employeePublicKey',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 48,
        pinned: 'left',
        filter: false,
      },
      {
        headerName: 'ID',
        field: 'employeePublicKey',
        cellRenderer: IdRenderer,
        width: 160,
      },
      {
        headerName: 'Name',
        field: 'firstName',
        cellRenderer: NameRenderer,
        valueGetter: (params) => {
          const { firstName, middleName, lastName } = params.data
          return [firstName, middleName, lastName].filter(Boolean).join(' ')
        },
      },
      {
        headerName: 'State',
        field: 'stateProvince',
      },
      {
        headerName: 'Rating Area',
        field: 'ratingArea',
      },
      {
        headerName: 'Enrollment Start',
        field: 'enrollmentStartDate',
        cellRenderer: DateRenderer,
      },
      {
        headerName: 'Enrollment End',
        field: 'enrollmentEndDate',
        cellRenderer: DateRenderer,
      },
      {
        headerName: 'Benefit Effective Date',
        field: 'benefitsEffectiveDate',
        cellRenderer: DateRenderer,
      },
      {
        headerName: 'Email',
        field: 'emailAddress',
      },
      {
        headerName: 'Phone',
        field: 'phoneNumber',
      },
      {
        headerName: 'Hire Date',
        field: 'hireDate',
        cellRenderer: DateRenderer,
      },
    ],
    []
  )

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    const selectedNodes = event.api.getSelectedNodes()
    const selectedData = selectedNodes.map((node) => node.data as Person)
    onSelectionChanged(selectedData)
    event.api.refreshCells()
  }

  const statusBar = useMemo<{ statusPanels: StatusPanelDef[] }>(
    () => ({
      statusPanels: [
        {
          statusPanel: CustomStatusBarComponent,
          statusPanelParams: {
            total,
            totalLabel,
          },
        },
      ],
    }),
    [total, totalLabel, members]
  )

  return (
    <div className="ag-theme-alpine" style={{ height: '560px', width: '100%' }}>
      <AgGridReact
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
        rowData={members}
        noRowsOverlayComponent={GridNoResultsOverlay}
        tooltipShowDelay={100}
        suppressMenuHide
        statusBar={statusBar}
        rowSelection="multiple"
        suppressRowClickSelection
        onSelectionChanged={handleSelectionChanged}
        onGridReady={onGridReady}
        quickFilterText={quickFilterText}
      />
    </div>
  )
}
